import { useTranslation } from "react-i18next";
import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { LabeledValue } from "components/designV2/labeledValue/labeledValue";
import { CustomIcon } from "components/icon/customIcon.component";
import SimflexityPayLogo from "images/logo/svg/simflexityPay-logo.svg";
import { PaymentMethod } from "model/Gig";
import {
  CompanyWorkerSalary,
  EmployeeCategory,
  WorkerSalaryType,
} from "model/Salary";
import {
  calculateMonthlyPayoutSalary,
  formatMonthlySalary,
} from "web-apps/company/components/common/salary/monthlyPayoutSalary.logic";
import { toEmploymentTypeLabel } from "web-apps/company/components/common/employmentType/employmentType.logic";
import { formatIsoDateOnlyInStockholm } from "web-apps/company/utils/timeAndDate";
import { Logo, LogoContainer } from "./salary.styled";
import {
  AvatarAndActions,
  RowCard,
  RowGrid,
  RowMetrics,
  RowTop,
} from "./salaryListRow.styles";

export interface SalaryListRowProps {
  salary: CompanyWorkerSalary;
  avatarUrl?: string;
  onSelectWorker: (workerId: number) => void;
}

export const SalaryListRow = ({
  salary,
  avatarUrl,
  onSelectWorker,
}: SalaryListRowProps): JSX.Element => {
  const { t } = useTranslation();

  const isTrainee = salary.employment.employmentType === "Trainee";
  const isSimflexityEmployee =
    salary.employment.employmentType === "SimflexityEmployee";
  const isSimflexityPay =
    salary.employment.paymentType === PaymentMethod.SIMFLEXITY_PAY;
  const payoutSalary = calculateMonthlyPayoutSalary({
    salary: salary.employment.salary,
    salaryType: salary.employment.salaryType,
    workPercentage: salary.employment.workPercentage,
  });

  const title = `${salary.firstName} ${salary.lastName}${
    !salary.isActive ? ` ${t("Salary.Inactive")}` : ""
  }`;
  const employeeCategoryLabel =
    salary.employment.employeeCategory === EmployeeCategory.UNDEFINED
      ? "-"
      : t(`Staff.employeeCategory${salary.employment.employeeCategory}`);

  const salaryLabel = isTrainee
    ? t("Salary.Salary")
    : payoutSalary == null
      ? t(`Salary.${salary.employment.salaryType}`)
      : t("Salary.PayoutSalary");
  const salaryValue = isTrainee
    ? "-"
    : salary.employment.salaryType === WorkerSalaryType.MONTHLY
      ? formatMonthlySalary(payoutSalary ?? salary.employment.salary)
      : `${salary.employment.salary} kr`;

  const workPercentageValue = isTrainee
    ? "-"
    : salary.employment.salaryType === WorkerSalaryType.MONTHLY
      ? `${salary.employment.workPercentage * 100}%`
      : t("Salary.Hours");

  const taxValue =
    isTrainee || isSimflexityEmployee ? "-" : salary.employment.taxValue;

  const startDate = formatIsoDateOnlyInStockholm(salary.employment.startDate);
  const applyFromValue = salary.isActive
    ? startDate
    : `${startDate} - ${formatIsoDateOnlyInStockholm(salary.employment.endDate)}`;

  return (
    <RowCard
      type="button"
      $isInactive={!salary.isActive}
      onClick={() => onSelectWorker(salary.workerId)}
      aria-label={t("Staff.ProfilePanelOpenLabel", {
        worker: `${salary.firstName} ${salary.lastName}`,
      })}
    >
      <RowGrid>
        <RowTop>
          <AvatarChip
            title={title}
            subTitle={employeeCategoryLabel}
            avatarUrl={avatarUrl}
            titleIcon={
              isSimflexityPay ? (
                <LogoContainer>
                  <Logo src={SimflexityPayLogo} alt="SimflexityPay logo" />
                </LogoContainer>
              ) : null
            }
          />
          <AvatarAndActions>
            <CustomIcon name="v2/right-chevron" size="18px" />
          </AvatarAndActions>
        </RowTop>

        <RowMetrics>
          <LabeledValue
            variant="small"
            value={salaryValue}
            label={salaryLabel}
          />
          <LabeledValue
            variant="small"
            value={workPercentageValue}
            label={t("Salary.WorkPercentage")}
          />
          <LabeledValue
            variant="small"
            value={taxValue.toString()}
            label={t("OrganizationalStructure.TaxTable")}
          />
          <LabeledValue
            variant="small"
            value={toEmploymentTypeLabel({
              employmentType: salary.employment.employmentType,
              translate: t,
            })}
            label={t("Staff.EmploymentType")}
          />
          <LabeledValue
            variant="small"
            value={applyFromValue}
            label={t("Salary.ApplyFrom")}
          />
        </RowMetrics>
      </RowGrid>
    </RowCard>
  );
};
